import { DTO, Types } from 'shared-lib';

// Tipos para parâmetros de API
export type PaginationParams = {
  page: number;
  limit: number;
  status?: string;
};

export type SearchParams = PaginationParams & {
  query: string;
};

export type FilterParams = PaginationParams & {
  filter: string;
};

// Tipos para respostas de API
export type ApiResponse<T = Types.JsonValue> = {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
};

export type PaginatedResponse<T = Types.JsonValue> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

// Tipos específicos para entidades
export type LoginRequest = {
  email: string;
  password: string;
};

export type RegisterRequest = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type RegisterFormDTO = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  confirmPassword: string;
};

export type EmailRequest = {
  email: string;
};

export type VerifyCodeRequest = {
  email: string;
  code: string;
};

export type ChangePasswordRequest = {
  email: string;
  newPassword: string;
  code: string;
};

// Tipos para Dashboard
export type DashboardData = {
  totalRides: number;
  totalDrivers: number;
  totalRiders: number;
  totalRevenue: number;
  onlineDrivers: number;
  activeRides: number;
  completedRides: number;
  cancelledRides: number;
};

// Tipos para Driver
export type DriverLocationRequest = {
  driverId: number;
};

export type DriverStatusUpdate = {
  status: Types.DriverStatusEnum;
};

export type CreateDriverRequest = {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  profileImageKey?: File;
  documentsKey?: File;
  addressDocumentKey?: File;
  documentsCriminalsKey?: File;
};

// Tipos para Vehicle
export type CreateVehicleRequest = {
  plateNumber: string;
  model: string;
  brand: string;
  year: number;
  color: string;
  categoryId: number;
  driverId: number;
  vehicleImg?: File;
  vehicleDoc?: File;
};

export type UpdateVehicleRequest = Partial<CreateVehicleRequest> & {
  id: number;
};

// Tipos para Rider
export type UpdateRiderStatusRequest = {
  id: number;
  status: Types.RiderStatusEnum;
};

// Tipos para Category
export type CreateCategoryRequest = {
  name: string;
  description?: string;
  urlImg?: File;
};

export type UpdateCategoryRequest = Partial<CreateCategoryRequest> & {
  id: number;
};


// Tipos para App Config
export type AppConfigRequest = {
  name: string;
  value: string | number | boolean;
  description?: string;
};

// Tipos para componentes
export type TableRow<T> = {
  original: T;
};

export type TableCellInfo<T> = {
  getValue: () => Types.JsonValue;
  row: TableRow<T>;
};

export type TableColumn<T = Types.JsonValue> = {
  header: string;
  accessor: keyof T | string;
  cell?: (info: TableCellInfo<T>) => React.ReactNode;
};

export type TablePagination = {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  gotoPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
};

export type TableProps<T = Types.JsonValue> = {
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  pagination?: TablePagination;
};

// Tipos para formulários
export type FormikProps<T = Types.JsonValue> = {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  setFieldValue: (field: keyof T, value: Types.JsonValue) => void;
  isSubmitting: boolean;
};

// Tipos para eventos
export type PageClickEvent = {
  selected: number;
};

export type SearchTargetField = {
  value: string;
};

export type SearchTarget = {
  search: SearchTargetField;
};

export type SearchSubmitEvent = {
  preventDefault: () => void;
  target: SearchTarget;
};

// Tipos para Chart components
export type ChartData = {
  name: string;
  value: number;
  color?: string;
};

export type ChartLegendOptions = {
  display?: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right';
};

export type ChartTooltipOptions = {
  enabled?: boolean;
};

export type ChartPluginsOptions = {
  legend?: ChartLegendOptions;
  tooltip?: ChartTooltipOptions;
};

export type ChartAxisOptions = {
  display?: boolean;
};

export type ChartScalesOptions = {
  x?: ChartAxisOptions;
  y?: ChartAxisOptions;
};

export type ChartOptions = {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  plugins?: ChartPluginsOptions;
  scales?: ChartScalesOptions;
};

export type ChartProps = {
  chartData: ChartData[];
  chartOptions: ChartOptions;
};

// Tipos para Error Handling
export type ApiError = {
  message: string;
  status?: number;
  code?: string;
  details?: Types.JsonValue;
};

// Tipos para File Upload
export type FileUploadProps = {
  file?: File;
  name: string;
  accept?: string;
  maxSize?: number;
  onFileChange: (file: File | null) => void;
};

// Tipos para Modal
export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
};

// Tipos para Status
export type EntityStatus = Types.AdminEntityStatusEnum;
export type RideStatus = Types.AdminRideStatusEnum;
export type RideFilterEnum = Types.RideFilterEnum;
export const RideFilterEnum = Types.RideFilterEnum;

export type RideListData = {
  rides: DTO.RideDTO[];
  totalCount: number;
};

export type RideListApiResponse = {
  message: string;
  data: RideListData;
};

export type RideTableRowDTO = {
  bookingDate: string;
  bookingTime: string;
  riderMobileNum: string;
  DriverMobileNum: string;
  fare?: number;
  platform?: string;
  status?: string;
  origin?: string;
  destination?: string;
  view?: number;
  driverId?: number | null;
  riderId?: number | null;
};

export type DeleteVehicleRequest = {
  id: number;
  profileImageKey?: string;
  documentsKey?: string[];
};

export type DeleteVehicleResponse = {
  message: string;
};

export type VehicleListData = {
  vehicles: DTO.VehicleDTO[];
  totalVehicles: number;
};

export type VehicleListApiResponse = {
  message: string;
  data: VehicleListData;
  totalVehicles: number;
};

export type VehicleSearchApiResponse = {
  message: string;
  data: DTO.VehicleDTO[];
  total: number;
};

export type VehicleTableNameDTO = {
  name: string;
  path: string;
};

export type VehicleTableActionDTO = {
  id: number;
  documentsKey?: string[];
  approved?: Types.ApprovalStatus;
  profileImageKey?: string;
};

export type VehicleTableRowDTO = {
  vehicleName: VehicleTableNameDTO;
  vehicleNumber?: string;
  vehicleType?: DTO.CategoryDTO;
  vehicleStatus?: Types.VehicleStatus;
  action: VehicleTableActionDTO;
  driverId?: number;
  driverName?: string;
  transportModes?: string[];
  primaryTransportMode?: string;
  vehicleTypeName?: string;
};

export type ImageWithFallbackProps = {
  src: string;
  alt: string;
  fallbackSrc: string;
};

// Re-export dos DTOs da shared-lib para facilitar o uso
export type Ride = DTO.RideDTO;
export type Driver = DTO.DriverDTO;
export type Rider = DTO.RiderDTO;
export type Vehicle = DTO.VehicleDTO;
export type Category = DTO.CategoryDTO;
export type Transaction = DTO.TransactionDTO;
export type Rating = DTO.RatingDTO;
export type AppConfig = DTO.AppDto;
export type ChatMessage = DTO.ChatMessageDTO;
export type FileData = DTO.FileDTO;
